import { Box, Skeleton, Slide, Typography, useTheme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { EllipsesMenu, FilterChips } from 'src/components';
import { NotificationStatus, useNotificationRecords } from 'src/graphql/entities/notification';
import {
  useInViewTrigger,
  useScrollFallbackWhenNotOverflowing,
  useScrollPaginateHandler,
} from 'src/utils/hooks';
import { useInboxArchive } from './hooks/use-inbox-archive';
import { useInboxFilters } from './hooks/use-inbox-filters';
import { useInboxMenuItems } from './hooks/use-inbox-menu-items';
import { useNotificationMenuItems } from './hooks/use-notification-menu-items';
import { useNotificationNavigation } from './hooks/use-notification-navigation';
import { InboxPaneItem } from './inbox-pane-item';
import {
  ARCHIVE_EXIT_TIMEOUT_MS,
  INBOX_FILTER_OPERATOR_DISPLAY,
} from './inbox-pane.constants';
import {
  ChipsRow,
  EmptyIcon,
  EmptyState,
  getHeadToolSx,
  Header,
  ItemTransitionWrapper,
  ScrollArea,
  SkeletonRow,
} from './inbox-pane.styles';

const DEFAULT_FILTERS = [
  { attribute: 'status', operator: '!=', value: NotificationStatus.Archived },
];

const ArchiveNotificationTransition = (
  props: TransitionProps & { children: React.ReactElement },
) => {
  const { children, in: inProp, ...transitionProps } = props;

  return (
    <ItemTransitionWrapper
      {...transitionProps}
      appear={false}
      enter={false}
      in={inProp}
      timeout={ARCHIVE_EXIT_TIMEOUT_MS}
    >
      <Slide
        appear={false}
        direction="right"
        enter={false}
        in={inProp}
        timeout={ARCHIVE_EXIT_TIMEOUT_MS}
        mountOnEnter
        unmountOnExit
      >
        <Box>{children}</Box>
      </Slide>
    </ItemTransitionWrapper>
  );
};

export const InboxPane = () => {
  const theme = useTheme();

  const {
    filters,
    chipConditions,
    chipQueryOptions,
    handleChipConditionsChange,
    deleteChipCondition,
    filterMenuOptions,
  } = useInboxFilters();

  const { notifications, loadMore, hasMore, isLoading, isLoadingMore } =
    useNotificationRecords({
      filters: [...DEFAULT_FILTERS, ...filters],
    });

  const { menuItems, markOneAsRead } = useInboxMenuItems();

  const { navigateToTarget, handleItemClick, isRowSelected } = useNotificationNavigation({
    markOneAsRead,
  });

  const { activeIndex, setActiveIndex, archiveAt, openNextAfterIfSelected } = useInboxArchive({
    notifications,
    isRowSelected,
    navigateToTarget,
  });

  const { getMenuItems } = useNotificationMenuItems();

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 300,
  });

  useScrollFallbackWhenNotOverflowing({
    containerRef: scrollAreaRef,
    onScroll: hasMore ? handleScroll : undefined,
    dataKey: notifications.length,
  });

  const inViewRef = useInViewTrigger<HTMLDivElement>({
    onEnter: () => loadMore(),
    enabled: !isLoading && hasMore && !isLoadingMore,
  });

  return (
    <>
      <Header>
        {filterMenuOptions?.length ? (
          <EllipsesMenu
            options={filterMenuOptions}
            icon="filter"
            iconLibrary="lucide"
            title="Filter"
            triggerTestId="inbox-pane-filter"
            sx={getHeadToolSx(theme)}
          />
        ) : null}
        {menuItems?.length ? (
          <EllipsesMenu
            options={menuItems}
            icon="more-vertical"
            iconLibrary="lucide"
            title="More"
            triggerTestId="inbox-pane-more"
            sx={getHeadToolSx(theme)}
          />
        ) : null}
      </Header>

      {chipConditions?.length > 0 && (
        <ChipsRow>
          <FilterChips
            autoValueSelector
            conditions={chipConditions}
            onConditionsChange={handleChipConditionsChange}
            deleteCondition={deleteChipCondition}
            queryOptions={chipQueryOptions}
            operatorDisplayOverrides={INBOX_FILTER_OPERATOR_DISPLAY}
          />
        </ChipsRow>
      )}

      <ScrollArea
        ref={scrollAreaRef}
        onScroll={handleScroll}
        data-testid="inbox-pane-scroll-area"
      >
        {!!isLoading &&
          // loading items should use indexes key
          Array.from({ length: 15 }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={idx}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="90%" height={14} />
              </Box>
              <Skeleton variant="text" width={28} height={14} />
            </SkeletonRow>
          ))}

        {!isLoading && notifications.length === 0 && (
          <EmptyState>
            <EmptyIcon name="inbox" />
            <Typography variant="caption">No notifications</Typography>
          </EmptyState>
        )}

        <TransitionGroup component={null}>
          {notifications.map((n, idx) => (
            <ArchiveNotificationTransition key={n.id}>
              <InboxPaneItem
                notification={n}
                isSelected={isRowSelected(n)}
                isActive={activeIndex === idx}
                menuItems={getMenuItems(n, { onArchived: () => openNextAfterIfSelected(n) })}
                onClick={() => handleItemClick(n)}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(null)}
                onSwipeArchive={() => archiveAt(idx)}
              />
            </ArchiveNotificationTransition>
          ))}
        </TransitionGroup>

        {!!isLoadingMore &&
          !!hasMore &&
          // loading items should use indexes key
          Array.from({ length: 10 }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={idx}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="90%" height={14} />
              </Box>
              <Skeleton variant="text" width={28} height={14} />
            </SkeletonRow>
          ))}

        {/* ensures progress when first pages collapse to few items after grouping */}
        <Box ref={inViewRef} sx={{ height: '1px' }} />
      </ScrollArea>
    </>
  );
};
